import { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator, Pressable, Linking } from "react-native";
import { useGlobalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";
import { UserType } from "@/types/db/users";
import Avatar from "@/components/Avatar";
import { StatusBadge } from "@/components/crew";

type UserData = Partial<UserType>;

interface ShiftUserData {
  id: string;
  status?: string;
  shift_position?: {
    position?: {
      name?: string;
    };
  };
}

export default function SupervisingUserDetailPage() {
  // useGlobalSearchParams (not Local): nested [shift] routes do not hand Local the parent param -> stuck loading
  const params = useGlobalSearchParams<{ shift: string; user: string }>();
  const shiftId = params.shift;
  const userId = params.user;
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserData | null>(null);
  const [shiftUser, setShiftUser] = useState<ShiftUserData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !shiftId) return;

    (async () => {
      setLoading(true);
      try {
        // Fetch user data directly from users table
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("id, first_name, last_name, preferred_name, email_address, meta_data")
          .eq("id", userId)
          .single();

        if (userError) throw userError;
        setUser(userData);

        // Also fetch shift_user data for status and position
        const { data: shiftUserData } = await supabase
          .from("shift_users")
          .select(
            `
            id,
            status,
            shift_position:shift_positions(
              position:positions(name)
            )
          `,
          )
          .eq("user_id", userId)
          .eq("shift_position.shift_id", shiftId)
          .single();

        if (shiftUserData) {
          setShiftUser({
            id: shiftUserData.id,
            status: shiftUserData.status,
            shift_position: shiftUserData.shift_position as any,
          });
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load user");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, shiftId]);

  const displayName = user
    ? user.preferred_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email_address
    : "Unknown User";

  // Extract phones from meta_data.phones array
  const cellPhone = user?.meta_data?.phones?.find((p) => p.type === "cell")?.number;
  const workPhone = user?.meta_data?.phones?.find((p) => p.type === "work")?.number;
  const hasAnyPhone = cellPhone || workPhone;

  const handleCall = (phoneNumber: string) => {
    Linking.openURL(`tel:${phoneNumber}`);
  };

  const handleEmail = () => {
    if (user?.email_address) {
      Linking.openURL(`mailto:${user.email_address}`);
    }
  };

  const handleMessage = (phoneNumber: string) => {
    Linking.openURL(`sms:${phoneNumber}`);
  };

  const getStatusVariant = (status?: string) => {
    switch (status?.toUpperCase()) {
      case "ACCEPTED":
        return "accepted";
      case "DECLINED":
        return "declined";
      case "COMPLETE":
        return "completed";
      case "PARTIAL":
        return "active";
      case "NO SHOW":
        return "declined";
      default:
        return "pending";
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !user) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="account-alert" size={48} color={colors.placeholder} />
        <ThemedText style={{ marginTop: 12, color: colors.textSecondary }}>{error || "User not found"}</ThemedText>
      </View>
    );
  }

  const positionName = shiftUser?.shift_position?.position?.name;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Profile Card */}
      <View style={[styles.profileCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Avatar */}
        <Avatar
          userId={user.id}
          firstName={user.first_name ?? undefined}
          lastName={user.last_name ?? undefined}
          size={80}
        />

        {/* Name */}
        <ThemedText style={styles.name}>{displayName}</ThemedText>

        {/* Email */}
        {user.email_address && (
          <ThemedText style={[styles.email, { color: colors.textSecondary }]}>{user.email_address}</ThemedText>
        )}

        {/* Shift Status & Position */}
        <View style={styles.badgeRow}>
          {shiftUser?.status && (
            <StatusBadge variant={getStatusVariant(shiftUser.status)} text={shiftUser.status} size="small" />
          )}
          {positionName && (
            <View style={[styles.positionBadge, { backgroundColor: colors.backgroundSecondary }]}>
              <MaterialCommunityIcons name="badge-account" size={14} color={colors.textSecondary} />
              <ThemedText style={[styles.positionText, { color: colors.textSecondary }]}>{positionName}</ThemedText>
            </View>
          )}
        </View>
      </View>

      {/* Contact Actions */}
      <View style={[styles.actionsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <ThemedText style={[styles.sectionTitle, { color: colors.textSecondary }]}>Contact</ThemedText>

        {/* Cell Phone */}
        {cellPhone && (
          <View style={styles.actionRow}>
            <View style={styles.actionContent}>
              <ThemedText style={styles.actionTitle}>Cell</ThemedText>
              <ThemedText style={[styles.actionSubtitle, { color: colors.textSecondary }]}>{cellPhone}</ThemedText>
            </View>
            <View style={styles.actionButtons}>
              <Pressable
                onPress={() => handleCall(cellPhone)}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="phone" size={20} color={colors.emerald?.text} />
              </Pressable>
              <Pressable
                onPress={() => handleMessage(cellPhone)}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.violet?.background, borderColor: colors.violet?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="message-text" size={20} color={colors.violet?.text} />
              </Pressable>
            </View>
          </View>
        )}

        {/* Work Phone */}
        {workPhone && (
          <View style={styles.actionRow}>
            <View style={styles.actionContent}>
              <ThemedText style={styles.actionTitle}>Work</ThemedText>
              <ThemedText style={[styles.actionSubtitle, { color: colors.textSecondary }]}>{workPhone}</ThemedText>
            </View>
            <View style={styles.actionButtons}>
              <Pressable
                onPress={() => handleCall(workPhone)}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="phone" size={20} color={colors.emerald?.text} />
              </Pressable>
            </View>
          </View>
        )}

        {/* Email */}
        {user.email_address && (
          <View style={styles.actionRow}>
            <View style={styles.actionContent}>
              <ThemedText style={styles.actionTitle}>Email</ThemedText>
              <ThemedText style={[styles.actionSubtitle, { color: colors.textSecondary }]} numberOfLines={1}>
                {user.email_address}
              </ThemedText>
            </View>
            <View style={styles.actionButtons}>
              <Pressable
                onPress={handleEmail}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.cyan?.background, borderColor: colors.cyan?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="email" size={20} color={colors.cyan?.text} />
              </Pressable>
            </View>
          </View>
        )}

        {/* No contact options */}
        {!hasAnyPhone && !user.email_address && (
          <View style={styles.noContact}>
            <MaterialCommunityIcons name="account-off" size={32} color={colors.placeholder} />
            <ThemedText style={[styles.noContactText, { color: colors.textSecondary }]}>
              No contact information available
            </ThemedText>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  profileCard: {
    alignItems: "center",
    padding: 24,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: 16,
  },
  name: {
    fontSize: 22,
    fontWeight: "700",
    marginTop: 12,
    marginBottom: 4,
  },
  email: {
    fontSize: 14,
    marginBottom: 12,
  },
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  positionBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  positionText: {
    fontSize: 12,
    fontWeight: "500",
  },
  actionsCard: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  actionContent: {
    flex: 1,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  actionTitle: {
    fontSize: 15,
    fontWeight: "500",
  },
  actionSubtitle: {
    fontSize: 12,
    marginTop: 1,
  },
  noContact: {
    alignItems: "center",
    padding: 24,
    gap: 8,
  },
  noContactText: {
    fontSize: 14,
  },
});
